'use strict';

/**
 * Showcase-only progress builder: mock snapshot → same mappers/builders as production.
 * Does not call RTK or the live PortalApi. Does not enable the global mock interceptor.
 */

import {
	flowScenarioForMockScenario,
	getMockPipelineStatusSnapshotForScenario,
	pipelineProgressKindForMockScenario,
} from './mocks/pipelineStatusFacts.mock';
import { mapApiPipelineStatusSnapshotPayload } from './usePipelineStatus';
import { composePipelineStatusLiveGraph } from './composePipelineStatusLiveGraph';
import { buildPipelineProgressBundleFromSnapshot } from './pipelineProgressFromSnapshot';
import { buildPipelineProgressModel } from './pipelineProgressModel';
import { buildCorePipelineProgressModel } from './pipelineCoreProgressModel';
import { CORE_PROGRESS_COPY, DISCOVERY_PROGRESS_COPY, KITS_PROGRESS_COPY } from './pipelineProgressCopy';
import { SINGLE_V7_PROJECT_FLOW_SCENARIO } from './pipelineStatusFlowScenarios';

const SHOWCASE_PROJECT_ID = '3fa85f64-5717-4562-b3fc-2c963f66afa6';

/**
 * @param {string} mockScenarioKey
 */
export function buildShowcasePipelineProgress(mockScenarioKey) {
	const kind = pipelineProgressKindForMockScenario(mockScenarioKey);
	const flowScenarioKey = flowScenarioForMockScenario(mockScenarioKey);
	const raw = getMockPipelineStatusSnapshotForScenario(mockScenarioKey);
	const mapped = mapApiPipelineStatusSnapshotPayload(raw);

	return buildPipelineProgressBundleFromSnapshot({
		kind,
		flowScenarioKey,
		facts: mapped.facts,
		iterationManifests: mapped.iterationManifests,
		definition: composePipelineStatusLiveGraph(mapped.graph),
		currentFlows: mapped.currentFlows,
		projectId: SHOWCASE_PROJECT_ID,
	});
}

/**
 * Synthetic loading / error cards — same View props shape, no network.
 * @param {'loading' | 'error'} synthetic
 */
export function buildSyntheticShowcaseProgress(synthetic) {
	const emptyCore = buildCorePipelineProgressModel([], { supported: false, copy: CORE_PROGRESS_COPY });
	const emptyDiscovery = buildCorePipelineProgressModel([], {
		supported: false,
		copy: DISCOVERY_PROGRESS_COPY,
	});
	const emptyKits = buildCorePipelineProgressModel([], {
		supported: false,
		copy: KITS_PROGRESS_COPY,
	});
	const emptyModel = buildPipelineProgressModel([], { supported: false });

	if (synthetic === 'loading') {
		return {
			kind: /** @type {const} */ ('v7'),
			model: emptyModel,
			coreModel: emptyCore,
			discoveryModel: emptyDiscovery,
			kitsModel: emptyKits,
			stages: [],
			isLoading: true,
			error: undefined,
		};
	}

	return {
		kind: /** @type {const} */ ('v7'),
		model: emptyModel,
		coreModel: emptyCore,
		discoveryModel: emptyDiscovery,
		kitsModel: emptyKits,
		stages: [],
		isLoading: false,
		error: new Error('Mock API error'),
	};
}

export { SHOWCASE_PROJECT_ID, SINGLE_V7_PROJECT_FLOW_SCENARIO };
